/* ============================================================================
   BOUTIQUE — sincronizacion con Shopify (solo lectura)
   ----------------------------------------------------------------------------
   Lee el catalogo de los endpoints publicos de la tienda. Ni tokens ni Admin
   API: solo /collections/{handle}/products.json y /products/{handle}.json.

   En LOCAL el navegador no puede pedir edisamp.net por CORS, asi que pasamos
   por el proxy del servidor de desarrollo (tools/servir.py).
   En PRODUCCION el Studio vive en el mismo dominio: poner BASE = '' y las
   mismas rutas funcionan sin proxy.
   ========================================================================== */

const BOUTIQUE = (() => {

  /* De donde se leen los productos, segun donde viva la pagina:
       localhost / red local -> el proxy de servir.py, porque el navegador no
                                deja pedir a edisamp.net desde otro origen
       edisamp.net           -> mismo dominio, ruta relativa, sin proxy
       cualquier otro sitio  -> directo a edisamp.net. Verificado que su JSON
                                publico manda access-control-allow-origin: *,
                                asi que vale desde GitHub Pages o donde sea.  */
  /* .LOCAL TAMBIÉN ES SU MAC. Franklin entra por
     `macbook-pro-de-franklin.local`, que no estaba en esta lista: el catálogo
     se pedía entonces a edisamp.net desde otro origen, el navegador lo
     bloqueaba y Safari decía « Load failed ». La pantalla Productos se quedaba
     en « Todos 0 » -- y desde `localhost`, donde yo probaba, no pasaba nunca.
     Es la misma regla que ya tenía shopify.js; aquí faltaba. */
  const h = location.hostname;
  const enLocal = ['localhost', '127.0.0.1', '::1', ''].includes(h)
    || /\.local$/i.test(h)
    || /^(192\.168\.|172\.(1[6-9]|2\d|3[01])\.|10\.)/.test(h);
  const enLaTienda = /(^|\.)edisamp\.net$/.test(h);
  const BASE = enLocal ? '/api/boutique'
             : enLaTienda ? ''
             : 'https://www.edisamp.net';

  /* v2: el producto normalizado lleva ahora `type` y `tags`. Un caché escrito
     con la forma vieja no los tiene, y sin cambiar la clave el admin habría
     enseñado « Sin tipo » durante diez minutos con pinta de estar roto. */
  const CLE_CACHE = 'edisamp-catalogue-v2';
  const DUREE_CACHE = 10 * 60 * 1000;          // 10 min

  /* ------------------------------------------------------------- red ---- */
  async function lire(chemin) {
    const r = await fetch(BASE + chemin, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) throw new Error(`${chemin} -> HTTP ${r.status}`);
    return r.json();
  }

  /* La foto que Shopify asocia a una variante, buscada por el lado de las
     imagenes. Si el producto no las tiene asignadas, devuelve null y el
     Studio ensena el hueco de siempre: no se inventa ninguna. */
  function fotoDeVariante(p, id) {
    const im = (p.images || []).find(x => x && Array.isArray(x.variant_ids)
                                        && x.variant_ids.indexOf(id) >= 0);
    return im ? (im.src || im) : null;
  }

  /* Normaliza un producto de Shopify a lo que el Studio necesita */
  function normaliser(p) {
    const variante = (p.variants || [])[0] || {};
    return {
      /* EL ID DE SHOPIFY, que no estaba y hacía falta. Sin él, la línea del
         carrito no puede decir de QUÉ producto es el diseño: llegaba el
         diseño y la variante, y para atar el pedido al proyecto había que
         adivinar por el handle. Franklin: « no quiero que el cliente termine
         el personalizador y después Shopify añada simplemente una taza
         genérica sin relación con el proyecto ». */
      id: p.id || null,
      handle: p.handle,
      titre: p.title,
      prix: parseFloat(variante.price || 0),
      devise: 'CHF',
      varianteId: variante.id || null,
      /* TODAS las variantes, no solo la primera. El punto 9 tiene que casar la
         talla y el acabado elegidos en el Studio con la variante real de
         Shopify: sin las opciones aqui, el add-to-cart mandaria siempre la
         primera y el cliente recibiria una talla que no pidio. */
      variantes: (p.variants || []).map(v => ({
        id: v.id,
        titre: v.title,
        prix: parseFloat(v.price || 0),
        disponible: v.available !== false,
        options: [v.option1, v.option2, v.option3].filter(x => x != null),
        /* LA FOTO DE CADA VARIANTE. Los cojines de Franklin son UN producto
           con cuatro « Modele » (1-4), y cada uno tiene su estampado: sin su
           imagen, el cliente elegiría el modelo a ciegas, por un número.

           Y ESO ES EXACTAMENTE LO QUE PASABA. Las cuatro fotos del cojín SÍ
           están asignadas a sus variantes en Shopify, pero este endpoint
           devuelve `featured_image: null` en cada una y pone la relación al
           revés, en `images[].variant_ids`. Mirando sólo un lado, el cliente
           veía cuatro cuadros vacíos numerados 1, 2, 3, 4 y no había manera de
           saber cuál era cuál. Es el mismo dato de Shopify leído por donde
           viene. */
        image: (v.featured_image && (v.featured_image.src || v.featured_image))
               || fotoDeVariante(p, v.id)
               || null,
      })),
      optionsNoms: (p.options || []).map(o => (typeof o === 'string' ? o : o.name)),
      images: (p.images || []).map(i => i.src || i),
      image: ((p.images || [])[0] || {}).src || null,
      /* EL TIPO Y LOS TAGS, tal cual los pone Franklin en Shopify.
         El tipo agrupa el catálogo del admin (tazas con tazas, llaveros con
         llaveros) y es además la mitad de la regla de las colecciones
         automáticas `personnalisables-*` de la tienda:
             theme:personnalisable  Y  type:mug  ->  Mugs personnalisables
         Se conserva el valor de Shopify sin traducirlo: inventar aquí una
         taxonomía paralela la desincronizaría de la tienda al primer cambio. */
      type: p.product_type || '',
      tags: Array.isArray(p.tags) ? p.tags
          : (typeof p.tags === 'string' ? p.tags.split(',').map(t => t.trim()) : []),
    };
  }

  /* ------------------------------------------------------------ cache --- */
  function lireCache() {
    try {
      const brut = localStorage.getItem(CLE_CACHE);
      if (!brut) return null;
      const c = JSON.parse(brut);
      if (Date.now() - c.date > DUREE_CACHE) return null;
      return c.produits;
    } catch (e) { return null; }
  }

  function ecrireCache(produits) {
    try {
      localStorage.setItem(CLE_CACHE, JSON.stringify({ date: Date.now(), produits }));
    } catch (e) { /* cuota llena: no es grave, se recargara */ }
  }

  function viderCache() {
    try { localStorage.removeItem(CLE_CACHE); } catch (e) {}
  }

  /* --------------------------------------------------------- catalogo --- */
  /* Union de: los productos de las colecciones configuradas + cualquier
     handle que aparezca en config-personnalisation.json.
     Ese rescate por handle importa: la coleccion personnalisables-t-shirts
     esta vacia hoy, y sin el la camiseta ya calibrada no apareceria. */
  async function chargerCatalogue(config, forcer) {
    if (!forcer) {
      const c = lireCache();
      if (c) return c;
    }

    const parHandle = new Map();
    const erreurs = [];

    for (const col of (config.collections || [])) {
      try {
        const d = await lire(`/collections/${col}/products.json?limit=250`);
        (d.products || []).forEach(p => {
          const n = normaliser(p);
          n.collections = (parHandle.get(n.handle) || {}).collections || [];
          n.collections.push(col);
          parHandle.set(n.handle, n);
        });
      } catch (e) { erreurs.push(`${col}: ${e.message}`); }
    }

    // rescate: productos configurados que no salieron en ninguna coleccion
    for (const handle of Object.keys(config.produits || {})) {
      if (parHandle.has(handle)) continue;
      try {
        const d = await lire(`/products/${handle}.json`);
        if (d.product) {
          const n = normaliser(d.product);
          n.collections = ['(hors collection)'];
          parHandle.set(n.handle, n);
        }
      } catch (e) { erreurs.push(`${handle}: ${e.message}`); }
    }

    const produits = [...parHandle.values()];
    if (produits.length) ecrireCache(produits);
    if (erreurs.length) console.warn('[EDISAMP] boutique:', erreurs);
    return produits;
  }

  /* ------------------------------------------------------- recherche ---- */
  /* TODOS los productos publicados de la tienda, no solo los de las
     colecciones. Franklin: « me gustaria poder elegir un producto de Shopify
     para subirlo manualmente, elijo lo que ya esta publicado para ponerlo a
     disposicion de la edicion ».
     Hasta ahora el admin solo veia lo que estuviera en las cuatro colecciones
     `personnalisables-*`, asi que para configurar cualquier otro producto habia
     que ir a Shopify, meterlo en una coleccion y volver. Esto lo salta.

     /products.json es publico y solo devuelve lo PUBLICADO, que es justo lo que
     se quiere: no tiene sentido ofrecer a los clientes la personalizacion de un
     borrador. Se pagina de 250 en 250 y se para en cuanto una pagina viene
     corta — sin eso, una tienda de 300 productos se quedaria en los 250
     primeros y el buscador mentiria por omision. */
  const PAGINAS_MAX = 8;          // 2000 productos; de sobra y con freno
  let _todos = null;

  async function tousLesProduits(forcer) {
    if (_todos && !forcer) return _todos;
    const out = [];
    for (let page = 1; page <= PAGINAS_MAX; page++) {
      const d = await lire(`/products.json?limit=250&page=${page}`);
      const l = d.products || [];
      l.forEach(x => out.push(normaliser(x)));
      if (l.length < 250) break;
    }
    _todos = out;
    return out;
  }

  /* Busca por titulo o por handle, sin acentos ni mayusculas: quien escribe
     « cafe » tiene que encontrar « Café ». */
  const sinTildes = t => String(t || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  async function chercher(terme, forcer) {
    const t = sinTildes(terme).trim();
    const l = await tousLesProduits(forcer);
    if (!t) return l;
    return l.filter(p => sinTildes(p.titre + ' ' + p.handle).includes(t));
  }

  /* ----------------------------------------------------------- merge ---- */
  /* Shopify manda en nombre, precio e imagenes. La config local manda en
     zonas, polices, couleurs y fondAutorise. Nunca al reves. */
  /* Un producto puede estar en tres estados, y conviene no confundirlos:
       sin config      -> « À configurer »
       config + brouillon -> configurado pero NO visible para el cliente
       config + publie    -> visible para el cliente
     `statut` por defecto es 'brouillon': configurar no es publicar. Publicar
     es una accion explicita de Franklin. */
  function fusionner(produits, config) {
    return produits.map(p => {
      const perso = (config.produits || {})[p.handle] || null;
      const statut = perso ? (perso.statut || 'brouillon') : null;
      return Object.assign({}, p, {
        configure: !!perso,
        statut,
        publie: statut === 'publie',
        perso: perso,
        defauts: config.defauts || {},
      });
    });
  }

  const configures    = liste => liste.filter(p => p.configure);
  const aConfigurer   = liste => liste.filter(p => !p.configure);
  const publies       = liste => liste.filter(p => p.publie);

  /* Config local del admin. El admin guarda en localStorage y el Studio leia
     solo el fichero del repo: por eso nada de lo que se configuraba llegaba
     nunca al cliente. Se lee aqui, y SOLO en localhost: en produccion la
     config vendra de los metafields de Shopify, no del navegador. */
  function configLocaleAdmin() {
    /* NO REDECLARAR `enLocal` AQUI. Tenia su propia expresion, que no conocia
       `.local` -- y Franklin entra por `macbook-pro-de-franklin.local`. Todo
       lo que configuraba y publicaba en el admin se guardaba bien, pero el
       Studio no lo leia nunca: abria el llavero y decia « este producto
       todavia no esta preparado para personalizar », porque solo veia los dos
       productos del fichero del repo. Se usa la del modulo, que es la unica
       que hay que mantener. */
    if (!enLocal) return null;
    try {
      const c = JSON.parse(localStorage.getItem('edisamp.admin.config') || 'null');
      const valida = c && typeof c === 'object'
        && c.produits && typeof c.produits === 'object'
        && c.defauts && typeof c.defauts === 'object';
      return valida ? c : null;
    } catch (e) { return null; }
  }

  /* ------------------------------------------------------------------------
     RESCATE POR METAFIELD — QUE EL BOTON Y EL STUDIO NO PUEDAN DESMENTIRSE
     ------------------------------------------------------------------------
     Habia DOS fuentes de verdad y se separaron solas:

       · el BOTON « Personnaliser » de la ficha lo decide el metafield
         `edisamp_perso.config` del producto, que se escribe en la tienda VIVA
         en cuanto Franklin pulsa « Écrire la config » ;
       · el STUDIO lee `assets/edisamp-config.js`, que es una FOTO del admin
         hecha al desplegar, y que no llega a la tienda hasta que se publica
         un tema nuevo.

     Entre lo uno y lo otro pueden pasar dias. El 19/09/2026 habia 18 productos
     con boton y 5 en la foto: Franklin pulso « Personnaliser » en la taza de
     animales y aparecio una camiseta. No es un despiste, es la consecuencia
     inevitable de tener dos listas que se escriben en momentos distintos.

     Asi que cuando el Studio no conoce el producto que le piden, va a
     buscarlo a la tienda: el mismo metafield que encendio el boton, servido
     por `templates/product.edisamp-config.liquid`. Si el metafield no esta o
     no esta publicado, no se inventa nada — se devuelve null y el Studio lo
     dice. La foto sigue valiendo como via rapida; esto es la red. */
  async function rescaterProduit(handle) {
    if (!handle) return null;
    let perso = null;
    try {
      /* NI UNA CABECERA `Accept`, Y NO ES UN OLVIDO. Shopify negocia el
         contenido de esta ruta: con `application/json` sirve la ficha del
         producto e ignora el `view` (4717 bytes en vez de 1812, y `statut`
         salia `undefined` — el rescate se negaba sin decir por que); con
         `text/plain, text/html` devuelve 404. Medido contra la tienda, lo
         unico que sirve la plantilla siempre es no pedir nada. */
      const r = await fetch(`${BASE}/products/${handle}?view=edisamp-config`,
                            { cache: 'no-store' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const t = (await r.text()).trim();
      /* Un tema sin la plantilla devuelve la ficha del producto en HTML: eso
         no es una config, y tragarselo daria un objeto vacio con pinta de
         producto configurado. */
      if (!t || t[0] !== '{') throw new Error('respuesta no es la config');
      perso = JSON.parse(t);
    } catch (e) {
      console.warn('[EDISAMP] rescate por metafield:', handle, e.message);
      return null;
    }
    if (!perso || perso.statut !== 'publie') return null;

    let brut = null;
    try {
      const d = await lire(`/products/${handle}.json`);
      brut = d.product ? normaliser(d.product) : null;
    } catch (e) {
      console.warn('[EDISAMP] rescate, ficha del producto:', handle, e.message);
      return null;
    }
    if (!brut) return null;

    return Object.assign({}, brut, {
      configure:true, statut:'publie', publie:true, perso,
      collections:['(rescate)'], rescate:true,
    });
  }

  return { chargerCatalogue, chercher, tousLesProduits,
           fusionner, configures, aConfigurer, publies,
           configLocaleAdmin, viderCache, lire, rescaterProduit, BASE };
})();
